import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldAlert } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContextType';
import PageHeader from '@/components/layout/PageHeader';
import DataTable, { type ColumnDef } from '@/components/shared/DataTable';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

interface AuditEvent {
  _id?: string;
  timestamp?: string;
  event_type?: string;
  action?: string;
  user_id?: string;
  user_email?: string;
  resource_type?: string;
  resource_id?: string;
  severity?: string;
  result?: string;
}

const fmtTs = (ts?: string) => {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
};
const shortId = (id?: string) => id ? `${id.substring(0, 8)}\u2026` : '—';

const severityBadge = (sev?: string) => {
  const v = sev?.toLowerCase();
  if (v === 'critical' || v === 'error') return <Badge variant="destructive" className="text-[11px]">{sev}</Badge>;
  if (v === 'warning') return <Badge variant="outline" className="text-[11px] border-amber-400 text-amber-700">{sev}</Badge>;
  return <Badge variant="secondary" className="text-[11px]">{sev ?? '—'}</Badge>;
};

const eventTypeBadge = (et?: string) => {
  if (!et) return <span className="text-xs text-slate-400">—</span>;
  const cls = et.startsWith('auth.') ? 'bg-blue-50 text-blue-700 border-blue-200'
    : et.startsWith('authz.') ? 'bg-orange-50 text-orange-700 border-orange-200'
    : et.startsWith('data.') ? 'bg-green-50 text-green-700 border-green-200'
    : et.startsWith('gdpr.') ? 'bg-purple-50 text-purple-700 border-purple-200'
    : 'bg-slate-50 text-slate-600 border-slate-200';
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[11px] font-mono ${cls}`}>{et}</span>;
};

export default function SettingsAuditLog() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isSysAdmin = user && ['SysAdmin', 'DevOps'].includes(user.role ?? '');
  const isAdmin = user && ['SysAdmin', 'DevOps', 'ClientAdmin'].includes(user.role ?? '');

  const today = new Date().toISOString().split('T')[0];
  const sevenDaysAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];

  const [fromDate, setFromDate] = useState(sevenDaysAgo);
  const [toDate, setToDate] = useState(today);
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');

  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [severities, setSeverities] = useState<string[]>([]);

  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const [verifyResult, setVerifyResult] = useState<{ verified: boolean; total_entries: number; issues: unknown[] } | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const [cleanupResult, setCleanupResult] = useState<number | null>(null);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanupError, setCleanupError] = useState<string | null>(null);

  useEffect(() => {
    api.get('/audit-log/event-types').then(({ data }) => {
      setEventTypes(data.event_types ?? []);
      setSeverities(data.severities ?? []);
    }).catch(() => {});
  }, []);

  const loadEvents = async () => {
    setLoading(true); setError(null); setLoaded(false);
    try {
      const params: Record<string, string> = { limit: '500', skip: '0' };
      if (fromDate) params.start_time = new Date(fromDate).toISOString();
      if (toDate) params.end_time = new Date(toDate + 'T23:59:59').toISOString();
      if (eventTypeFilter) params.event_types = eventTypeFilter;
      if (severityFilter) params.severity = severityFilter;
      if (userIdFilter.trim()) params.user_id = userIdFilter.trim();
      const { data } = await api.get('/audit-log/events', { params });
      setEvents(data.events ?? []);
      setLoaded(true);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e?.response?.data?.detail ?? t('pages.settingsAuditLog.errorLoad'));
    } finally { setLoading(false); }
  };

  const verifyIntegrity = async () => {
    setVerifying(true); setVerifyError(null); setVerifyResult(null);
    try {
      const body: Record<string, string> = {};
      if (fromDate) body.start_time = new Date(fromDate).toISOString();
      if (toDate) body.end_time = new Date(toDate + 'T23:59:59').toISOString();
      const { data } = await api.post('/audit-log/verify-integrity', body);
      setVerifyResult(data);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setVerifyError(e?.response?.data?.detail ?? t('pages.settingsAuditLog.errorVerify'));
    } finally { setVerifying(false); }
  };

  const cleanupLogs = async () => {
    if (!window.confirm(t('pages.settingsAuditLog.cleanupConfirm'))) return;
    setCleaningUp(true); setCleanupError(null); setCleanupResult(null);
    try {
      const { data } = await api.delete('/audit-log/cleanup');
      setCleanupResult(data.deleted_count ?? 0);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setCleanupError(e?.response?.data?.detail ?? t('pages.settingsAuditLog.errorCleanup'));
    } finally { setCleaningUp(false); }
  };

  const errorCount = events.filter(e => ['error', 'critical'].includes(e.severity?.toLowerCase() ?? '')).length;
  const warnCount = events.filter(e => e.severity?.toLowerCase() === 'warning').length;
  const uniqueUsers = new Set(events.map(e => e.user_id).filter(Boolean)).size;

  const cols: ColumnDef<AuditEvent>[] = [
    { key: 'timestamp', label: t('pages.settingsAuditLog.colTimestamp'), sortable: true,
      render: r => <span className="font-mono text-xs whitespace-nowrap">{fmtTs(r.timestamp)}</span> },
    { key: 'event_type', label: t('pages.settingsAuditLog.colEventType'), sortable: true,
      render: r => eventTypeBadge(r.event_type) },
    { key: 'action', label: t('pages.settingsAuditLog.colAction'),
      render: r => <span className="text-xs text-slate-600 max-w-[200px] truncate block" title={r.action}>{r.action ?? '—'}</span> },
    { key: 'user_id', label: t('pages.settingsAuditLog.colUser'),
      render: r => <span className="font-mono text-xs" title={r.user_email ?? r.user_id}>{r.user_email ?? shortId(r.user_id)}</span> },
    { key: 'resource_type', label: t('pages.settingsAuditLog.colResource'),
      render: r => r.resource_type
        ? <span className="text-xs">{r.resource_type}{r.resource_id ? ` / ${shortId(r.resource_id)}` : ''}</span>
        : <span className="text-slate-300">—</span> },
    { key: 'severity', label: t('pages.settingsAuditLog.colSeverity'), sortable: true,
      render: r => severityBadge(r.severity) },
    { key: 'result', label: t('pages.settingsAuditLog.colResult'),
      render: r => r.result
        ? <Badge variant={r.result === 'success' ? 'secondary' : 'destructive'} className="text-[11px]">{r.result}</Badge>
        : <span className="text-slate-300">—</span> },
  ];

  if (!isAdmin) return null;

  return (
    <>
      <PageHeader title={t('pages.settingsAuditLog.title')} subtitle={t('pages.settingsAuditLog.subtitle')} />

      <div className="px-6 pb-6 space-y-6 pt-4">
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-700 pb-1 border-b border-slate-200">{t('pages.settingsAuditLog.sectionEvents')}</h2>

          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <label className="text-xs text-slate-500">{t('pages.settingsAuditLog.filterFrom')}</label>
              <input type="date" value={fromDate} max={today} onChange={e => setFromDate(e.target.value)}
                className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-400" />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-slate-500">{t('pages.settingsAuditLog.filterTo')}</label>
              <input type="date" value={toDate} max={today} onChange={e => setToDate(e.target.value)}
                className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-400" />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-slate-500">{t('pages.settingsAuditLog.filterEventType')}</label>
              <select value={eventTypeFilter} onChange={e => setEventTypeFilter(e.target.value)}
                className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-400">
                <option value="">{t('pages.settingsAuditLog.allTypes')}</option>
                {eventTypes.map(et => <option key={et} value={et}>{et}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-slate-500">{t('pages.settingsAuditLog.filterSeverity')}</label>
              <select value={severityFilter} onChange={e => setSeverityFilter(e.target.value)}
                className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-400">
                <option value="">{t('pages.settingsAuditLog.allSeverities')}</option>
                {severities.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {isSysAdmin && (
              <div className="space-y-1">
                <label className="text-xs text-slate-500">{t('pages.settingsAuditLog.filterUserId')}</label>
                <input type="text" value={userIdFilter} onChange={e => setUserIdFilter(e.target.value)}
                  placeholder={t('pages.settingsAuditLog.filterUserIdPlaceholder')}
                  className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-400 w-52" />
              </div>
            )}
            <Button size="sm" onClick={loadEvents} disabled={loading}>
              {loading ? t('pages.settingsAuditLog.loading') : t('pages.settingsAuditLog.btnLoad')}
            </Button>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          {loaded && (
            <div className="flex gap-4 text-xs text-slate-500">
              <span><span className="font-semibold text-slate-800">{events.length}</span> {t('pages.settingsAuditLog.statsTotal')}</span>
              <span><span className="font-semibold text-red-600">{errorCount}</span> {t('pages.settingsAuditLog.statsErrors')}</span>
              <span><span className="font-semibold text-amber-600">{warnCount}</span> {t('pages.settingsAuditLog.statsWarnings')}</span>
              <span><span className="font-semibold text-slate-800">{uniqueUsers}</span> {t('pages.settingsAuditLog.statsUsers')}</span>
            </div>
          )}

          {events.length > 0 ? (
            <DataTable columns={cols} data={events} />
          ) : loaded ? (
            <DataTable columns={cols} data={[]} emptyIcon={ShieldAlert}
              emptyTitle={t('pages.settingsAuditLog.noEvents')}
              emptyDescription={t('pages.settingsAuditLog.noEventsDescription')} />
          ) : null}
        </section>

        {isSysAdmin && (
          <>
            <Separator />
            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-slate-700 pb-1 border-b border-slate-200">{t('pages.settingsAuditLog.sectionAdmin')}</h2>
              <div className="flex flex-wrap items-start gap-6">
                <div className="space-y-1.5">
                  <Button size="sm" variant="outline" onClick={verifyIntegrity} disabled={verifying}>
                    {verifying ? t('pages.settingsAuditLog.verifying') : t('pages.settingsAuditLog.btnVerify')}
                  </Button>
                  {verifyResult && (
                    verifyResult.verified
                      ? <p className="text-xs text-green-700">{t('pages.settingsAuditLog.verifyOk', { count: verifyResult.total_entries })}</p>
                      : <p className="text-xs text-red-600">{t('pages.settingsAuditLog.verifyFailed', { count: verifyResult.issues.length })}</p>
                  )}
                  {verifyError && <p className="text-xs text-red-600">{verifyError}</p>}
                </div>
                <div className="space-y-1.5">
                  <Button size="sm" variant="outline" className="border-red-200 text-red-700 hover:bg-red-50" onClick={cleanupLogs} disabled={cleaningUp}>
                    {cleaningUp ? t('pages.settingsAuditLog.loading') : t('pages.settingsAuditLog.btnCleanup')}
                  </Button>
                  {cleanupResult !== null && <p className="text-xs text-slate-600">{t('pages.settingsAuditLog.cleanupDone', { count: cleanupResult })}</p>}
                  {cleanupError && <p className="text-xs text-red-600">{cleanupError}</p>}
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </>
  );
}
